export const projectsArticles = [
    {
        id: 'projects.overview',
        title: 'Projects Overview',
        category: 'Projects',
        order: 1,
        summary: 'Asana-style project workspace — every job is a project, with List, Board, Timeline, Calendar and Files views',
        content: `
The Projects page is a project workspace styled after Asana. The left **project rail** lists your projects, and selecting one opens its workspace with a header and a row of view tabs.

**Every job is a project.** All of your jobs appear in the rail automatically. Opening a job for the first time turns it into a project (seeding the default task columns), so you can plan work against any job without setting anything up first.

**View tabs (top of the workspace):**

- **List** - the default Asana-style task list, grouped into collapsible sections
- **Board** - a Kanban board, one column per status
- **Timeline** - a Gantt timeline of task dates and dependencies
- **Calendar** - tasks placed on a monthly calendar
- **Files** - the documents saved against the project's job

**Add task** (top right) and the per-section **Add task** rows create tasks. The **"…"** menu holds Edit, Duplicate, Archive, Delete, and **Import / paste tasks**.
    `,
        expandableSections: [
            {
                title: 'The project rail',
                content: `
The left rail is how you move between projects:

- **Search** projects by name or job number, and **Filter** by status, priority or owner
- Projects linked to the same job are **grouped under that job**; click the job header to expand or collapse its phases
- **+ New project** creates a blank project, one from a template, or imports tasks from a file or the clipboard
- Click a job that has no project yet to open it — it becomes a real project on the spot
- Collapse the rail with the chevron at the top to make more room

On a narrow screen the rail becomes a drawer you open from the menu button in the workspace header.
        `,
            },
            {
                title: 'List view',
                content: `
The **List** view groups tasks into collapsible **sections** (these are the same as the Board's columns — To Do, In Progress, Review, Done, plus any you add).

Each task row lets you edit in place — no need to open a dialog:

- **Completion circle** - click to mark a task done (it moves to the Done section)
- **Task name** - click the text to rename it
- **Assignee, Due date, Priority, Status** - click each cell to change it
- **Drag** a row (grab anywhere on it) to reorder within a section, or drop it on another section to change its status
- **Add task** at the bottom of any section, or use the header's Add task button
- Open the full task details with the expand icon that appears on hover

Changes save automatically.
        `,
            },
            {
                title: 'Paste a list of tasks',
                content: `
You can create many tasks at once by pasting — just like Asana:

1. In the **List** view, click **Add task** under a section
2. Paste a multi-line list copied from a text file or a single column in Excel
3. Each line becomes a task in that section (the first column is used as the task name)

For spreadsheets with several columns (name, status, priority, due date, assignee), use the workspace **"…" menu → Import / paste tasks**. Paste your rows, map the columns, preview, and add them all to the project.
        `,
            },
            {
                title: 'Board, Timeline and Calendar',
                content: `
The other view tabs show the same tasks in different ways:

- **Board** - a Kanban board; drag cards between columns to change status, and use the quick-add to create a card in a column
- **Timeline** - a Gantt chart; drag a bar's ends to change dates, or drag the whole bar to reschedule
- **Calendar** - tasks shown on their due dates; click a day to add a task on that date

Switching tabs keeps you in the same project; the tab you're on is remembered in the page address.
        `,
            },
            {
                title: 'Files',
                content: `
The **Files** tab lists every document saved against the project's job — drawings, PDFs, spreadsheets, photos and more.

- Search by file name or discipline
- Click a file to download it
- If a project isn't linked to a job, the Files tab explains there are no shared files to show

Files come straight from the job's documents, so anything uploaded on the Documents page or against the job appears here automatically.
        `,
            },
            {
                title: 'Naming projects and adding phases',
                content: `
Give projects descriptive names, and split a job into phases when you need separate boards:

**Rename a project**
1. Click the project name at the top of the workspace
2. Type any name, or use the preset chips to drop in the job **Address**, **Job number**, **Job name**, or a phase label (**Sales, Preliminary, Construction, Handover, Maintenance**)
3. Press Enter to save

**Add a phase to a job**
- Hover a job in the rail and click the **+**, or use **Add phase** under a job that already has several projects
- Choose a phase (Sales / Preliminary / Construction / Handover / Maintenance) or **Custom…** for your own name
- A new project is created for that job and grouped with its other phases in the rail

Each phase is a full, independent project with its own List, Board, Timeline, Calendar, Files and tasks.
        `,
            },
        ],
        keywords: [
            'projects', 'project', 'workspace', 'list', 'board', 'kanban', 'gantt', 'timeline', 'calendar',
            'files', 'documents', 'tasks', 'sections', 'drag', 'reorder', 'paste', 'import', 'rename',
            'phase', 'phases', 'address', 'job', 'asana', 'assignee', 'due date', 'priority', 'status',
        ],
        route: '/projects',
        relatedArticles: ['jobs.overview', 'documents.overview', 'dashboard.overview'],
    },
    {
        id: 'projects.tasks',
        title: 'Working with Tasks',
        category: 'Projects',
        order: 2,
        summary: 'Creating and editing tasks — the New/Edit Task dialog with General, Schedule, Predecessors and Notes tabs',
        content: `
In the **List view**, click a task name to open its **details pane** on the right (the list stays visible — Asana-style). Edit anything in place — it saves as you go. Use **↑/↓** to move between tasks while the pane is open, and **Esc** to close it; the pencil icon renames a task inline without opening the pane.

**Project templates** capture a task list you reuse — tick **Template** when creating a project and build its tasks, or turn a real project into one with **⋯ → Save as template** (task dates become day offsets from the earliest task). Use **+ New Project → From Template** to start a new project from one (tasks scheduled from your chosen start date), or **⋯ → Add from template** to append a template's tasks into the project you're in.

**My Tasks** (in the side menu, under Dashboard) gathers everything assigned to *you* in one place — document **approvals and reviews** you can action inline (approve, reject, delegate) and **project tasks** that open directly in their project. Tasks are assigned to a staff member from the task's **Assigned to** dropdown; overdue items are flagged in red.

Tasks are the work items inside a project. The same tasks appear in every view tab — **List**, **Board**, **Timeline** and **Calendar** — so you can edit them wherever suits.

**Creating a task:** click **Add task** at the top right of the workspace, or click a day in the **Calendar** view to start a task due on that date.

**Editing a task:** open the full task dialog by clicking a card on the **Board**, a bar on the **Timeline**, a task on the **Calendar**, or the expand icon (**Open details**) that appears when you hover a row in the **List** view.

The dialog is organised into four tabs — **General**, **Schedule**, **Predecessors** (existing tasks only) and **Notes**. **Title** is the only required field. Finish with **Create Task** (new) or **Save Changes** (editing); the **Delete** button on the left removes the task.
    `,
        expandableSections: [
            {
                title: 'General tab — the core fields',
                content: `
- **Title** (required) and **Description**
- **Status** - To Do, In Progress, Review, Done or Blocked
- **Priority** - Low, Normal, High or Urgent
- **Progress** - drag the slider (5% steps) or use the shortcut buttons: 0%, 25%, 50%, 75%, 100%
- **Estimated Hours** - expected effort, in hours
- **Assignee** - who's responsible (type a name)
- **Parent Task** - nest this task under another, or leave as **No parent (top level)**
- **Tags** - type a label and press Enter (or the **+** button); click the **×** on a tag to remove it
        `,
            },
            {
                title: 'Schedule tab — dates, milestones and constraints',
                content: `
- **Start Date** and **Due Date** - when the task runs; these drive the Timeline bar and the Calendar placement
- **Mark as Milestone** - shows the task as a milestone diamond on the Timeline
- **Deadline** - a target finish; the Timeline bar turns red if the task finishes after it
- **Constraint** - an MS Project-style scheduling constraint: **As soon as possible** (the default, no constraint), **Start no earlier than**, **Finish no later than**, **Must start on**, or **Must finish on**. All except "As soon as possible" take a date, and the constraint is shown as a pin on the Timeline (informational)
        `,
            },
            {
                title: 'Predecessors — linking tasks',
                content: `
Predecessors are tasks that must finish before this task can start. The **Predecessors** tab appears when editing an existing task (a badge shows how many links it has).

To link tasks, use **Add Predecessor**:

1. **Task** - pick the predecessor from the project's other tasks
2. **Type** - Finish-to-Start (the usual), Start-to-Start, Finish-to-Finish, or Start-to-Finish
3. **Lag (days)** - an optional gap; use a negative number for overlap
4. Click **Add**

Existing links are listed with their type (FS, SS, FF, SF) and any lag; the **×** button removes a link. Dependencies are drawn as arrows between bars on the Timeline.
        `,
            },
            {
                title: 'Notes and quick edits',
                content: `
The **Notes** tab holds free-form notes, comments or details that don't belong in the short description.

You don't always need the dialog:

- **List** - click a cell (assignee, due date, priority, status) to change it in place, or the completion circle to mark it done
- **Board** - drag a card to another column to change its status
- **Timeline** - drag a bar to reschedule, or drag its ends to change the dates

Changes save automatically.
        `,
            },
        ],
        screenshots: [
            {
                src: 'projects/projects-task-modal.png',
                alt: 'The New Task dialog showing the General tab with title, status, priority, progress, assignee and tags',
                caption: 'The task dialog — General, Schedule, Predecessors and Notes tabs',
            },
        ],
        keywords: [
            'task', 'tasks', 'new task', 'edit task', 'milestone', 'predecessor', 'dependency',
            'constraint', 'deadline', 'progress', 'assignee', 'tags',
        ],
        route: '/projects',
        relatedArticles: ['projects.overview', 'dashboard.overview'],
    },
];
